import {useEffect} from 'react';
import {LoadingStates} from '@Utils/loading.util';
import {AiChatImageDownloadEvent} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import type {AiChatImageDownloadCompletedEventDetail, AiChatImageDownloadStartedEventDetail} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';
import type {GeneratedImagePartActionsLoading} from '@/ai-chat/providers/ai-chat-provider.types';

interface UseAiChatImageDownloadEventsParams {
  updateLoadingStatesForImageActions: (chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>) => void;
  refreshCredits: () => void;
}

export function useAiChatImageDownloadEvents({updateLoadingStatesForImageActions, refreshCredits}: UseAiChatImageDownloadEventsParams): void {
  useEffect(() => {
    const handleDownloadStarted = (event: Event): void => {
      const {chatId, hashedId} = (event as CustomEvent<AiChatImageDownloadStartedEventDetail>).detail;
      updateLoadingStatesForImageActions(chatId, hashedId, {downloadLoadingState: LoadingStates.LOADING});
    };

    const handleDownloadCompleted = (event: Event): void => {
      const {chatId, hashedId, isEnhanced, success} = (event as CustomEvent<AiChatImageDownloadCompletedEventDetail>).detail;
      updateLoadingStatesForImageActions(chatId, hashedId, {downloadLoadingState: LoadingStates.NOT_LOADED});

      if (success && isEnhanced) {
        refreshCredits();
      }
    };

    window.addEventListener(AiChatImageDownloadEvent.STARTED, handleDownloadStarted);
    window.addEventListener(AiChatImageDownloadEvent.COMPLETED, handleDownloadCompleted);

    return (): void => {
      window.removeEventListener(AiChatImageDownloadEvent.STARTED, handleDownloadStarted);
      window.removeEventListener(AiChatImageDownloadEvent.COMPLETED, handleDownloadCompleted);
    };
  }, [updateLoadingStatesForImageActions, refreshCredits]);
}
